import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import ListItemSecondaryAction from "@material-ui/core/ListItemSecondaryAction";
import IconButton from "@material-ui/core/IconButton";
import { useTheme } from "@material-ui/core/styles";
import { Doughnut } from "react-chartjs-2";
import VisibilityOutlined from "@material-ui/icons/VisibilityOutlined";
import PlaylistAddCheck from "@material-ui/icons/PlaylistAddCheckOutlined";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { isMenuAllowed } from "components/HasPermission";
import { get } from "lodash";
import Grid from "@mui/material/Grid";
import { Box, Typography } from "@mui/material";

import {
	fetchInvoiceIndicators,
	getFilterInvoiceStatus,
} from "../../../../redux/actions/invoices/index";

import DashboardCard from "../../../../components/DashboardCard/index";

import useStyles from "./style";
import { updateFilter } from "../../../../redux/filterReducer";
import CircularLoader from "../../../../components/Loaders/CircularLoader";

const dispalyModes = ["STATUS", "EXTRACTION_LEVEL"];

// const statusIndicators = [
// 	[{ code: "TO_VERIFY", label: "To verify" }],
// 	[{ code: "TO_CONTROL", label: "To control" }],
// 	[{ code: "EXPORTED", label: "invoice.status.exported" }],
// ];

const extractionLevelIndicators = ["N1", "N2", "N3"];

function MyTasks(props) {
    const classes = useStyles();
    const theme = useTheme();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const history = useHistory();

    const currentUser = useSelector(({ requests }) =>
        get(requests, "queries.FETCH_CURRENT_USER.data")
    );
    const currentUserPermissions = React.useMemo(() => get(currentUser, "permissions"), [
        currentUser,
    ]);
    const { dashboardStart, dashboardEnd } = useSelector(({ filterInvoices }) => filterInvoices);
    const [display] = React.useState(dispalyModes[0]);
    const rowsPerPage = localStorage.getItem("invoices_pageSize_default")
        ? localStorage.getItem("invoices_pageSize_default")
        : process.env.REACT_APP_DEFAULT_PAGE_SIZE_LIST_INVOICE;

    const indicators = useSelector(
        ({ requests }) =>
            requests.queries.FETCH_COUNT_INVOICE_INDICATOR &&
            requests.queries.FETCH_COUNT_INVOICE_INDICATOR.data
    );

    useEffect(() => {
        dispatch(fetchInvoiceIndicators(dashboardStart, dashboardEnd));
        dispatch(getFilterInvoiceStatus());
    }, [dashboardStart, dashboardEnd]);

    const onClickShowAll = (status, extractionLvl) => {
        dispatch(
            updateFilter({
                searchBy: "",
                status: status ? [status] : [], 
                startIssueDate: null,
                startSaveDate: null,
                startCreatedDate: null,
                endIssueDate: null,
                endSaveDate: null,
                endCreatedDate: null,
                pageNo: 0,
                pageSize: rowsPerPage,
                extractionLvl,
            })
        );
        history.push("/invoices", { fromDashboard: true });
    };

    const buildChart = (values, labels, colors) => {
        const hasData = values.some(v => v > 0);

        if (!hasData) {
            return {
                labels: ["No data"],
                datasets: [{
                    data: [1],
                    backgroundColor: ["#e0e0e0"],
                    borderWidth: 0
                }],
                hasData: false
            };
        }

        return {
            labels,
            datasets: [{
                data: values,
                backgroundColor: colors,
                borderWidth: 0
            }],
            hasData: true
        };
    };

    const getData = () => {
        const statusColors = [
            theme.custom?.color?.color4,
            "#ff9800",
            "#4caf50",
            theme.custom?.color?.error
        ];

        const extractionColors = [
            theme.custom?.color?.color1,
            theme.custom?.color?.color2,
            theme.custom?.color?.color3,
        ];

        const total = indicators ? indicators.totalInvoices : 0;
        const inProgress = indicators ? indicators.totalInvoicesInProgress : 0;
        const exported = indicators ? indicators.totalInvoicesToExport : 0;
        const rejected = indicators ? indicators.totalInvoicesToReject : 0;

        const getPercent = (val) => (total > 0 ? Math.round((val / total) * 100) : 0);

        if (display === "STATUS") {
            const gridLabels = [t("received"), t("in_progress"), t("exported"), t("rejected")];
            const gridValues = [total, inProgress, exported, rejected];
            const percentages = [null, getPercent(inProgress), getPercent(exported), getPercent(rejected)];

            const chartLabels = gridLabels.slice(1); 
            const chartValues = gridValues.slice(1); 
            const chartColors = statusColors.slice(1);

            return {
                isStatus: true,
                grid: {
                    labels: gridLabels,
                    values: gridValues,
                    colors: statusColors,
                    percentages
                },
                chart: buildChart(chartValues, chartLabels, chartColors)
            };
        }

        return {
            isStatus: false,
            grid: {
                labels: [t("total_extract"), t("partial_extract"), t("not_extract")],
                values: [
                    indicators ? indicators.totalInvoicesELN1 : 0,
                    indicators ? indicators.totalInvoicesELN2 : 0,
                    indicators ? indicators.totalInvoicesELN3 : 0,
                ],
                colors: extractionColors,
                percentages: []
            },
            chart: buildChart(
                [
                    indicators ? indicators.totalInvoicesELN1 : 0,
                    indicators ? indicators.totalInvoicesELN2 : 0,
                    indicators ? indicators.totalInvoicesELN3 : 0,
                ],
                [t("total_extract"), t("partial_extract"), t("not_extract")],
                extractionColors
            )
        };
    };

    const data = getData();

    return (
        <DashboardCard
            title={t("overview")}
            icon={PlaylistAddCheck}
            className={classes.card}
            {...props}
        >
            {data && indicators ? (
                <div>
                    <Box display="flex" justifyContent="center" mb={10} minHeight={150}>
                        <Doughnut
                            data={data.chart}
                            width={500}
                            options={{
                                legend: { display: false },
                                cutoutPercentage: 70,
                                maintainAspectRatio: false,
                                tooltips: {
                                    enabled: data.chart.hasData
                                }
                            }}
                        />
                    </Box>

                    {display === "STATUS" ? (
                        <Grid container spacing={1} className={classes.kpiContainer}>
                            {data.grid.labels.map((label, index) => (
                                <Grid item xs={6} sm={3} key={index}>
                                    <Box className={classes.kpiItem}>
                                        <Typography
                                            className={classes.kpiValue}
                                            variant="h6"
                                            style={{ color: data.grid.colors[index] }}
                                        >
                                            {data.grid.values[index]}
                                        </Typography>
                                        <Typography variant="subtitle2" style={{
                                            fontSize: '0.875rem',
                                            fontFamily: 'Rubik, sans-serif',
                                            fontWeight: 500,
                                            lineHeight: 1.57, color: 'rgba(0, 0, 0, 0.54)'
                                        }}>
                                            {label}
                                        </Typography>

                                        {data.grid.percentages[index] !== null && (
                                            <Typography variant="caption" style={{color: "#3c4d6f"}}>
                                                ({data.grid.percentages[index]}%)
                                            </Typography>
                                        )}
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>
                    ) : (
                        <List>
                            {data.grid.labels.map((label, index) => (
                                <ListItem key={index} className={clsx(!(index % 2) && classes.bgLight)}>
                                    <ListItemText
                                        primary={data.grid.values[index]}
                                        secondary={label}
                                        secondaryTypographyProps={{ variant: "subtitle1" }}
                                        style={{ color: data.grid.colors[index] }}
                                    />
                                    <ListItemSecondaryAction>
                                        {isMenuAllowed(currentUserPermissions, "Invoices list") &&
                                            <IconButton
                                                edge="end"
                                                aria-label="view"
                                                onClick={() => onClickShowAll(null, extractionLevelIndicators[index])}
                                            >
                                                <VisibilityOutlined />
                                            </IconButton>
                                        }
                                    </ListItemSecondaryAction>
                                </ListItem>
                            ))}
                        </List>
                    )}
                </div>
            ) : (
                <Box display="flex" justifyContent="center" p={3}>
                    <CircularLoader />
                </Box>
            )}
        </DashboardCard>
    );
}

export default MyTasks;
